import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { Controller, useForm } from "react-hook-form";
import ClientService from 'services/ClientService';
import DynamicTradeService from 'services/DynamicTradeService';
import Loader from '../../parts/Loader';

/* ── Helpers ──────────────────────────────────────────────── */
const fmt = (val, dp = 2) => (val != null ? Number(val).toFixed(dp) : '—');

const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
};

/* ── Strategy colour map (exact strings from backend) ──── */
const STRATEGY_COLOR_MAP = {
    'TP':               { color: '#0ecb81', bg: '#0a2e1e' },
    'SL':               { color: '#f87171', bg: '#3d1a1a' },
    'TSL':              { color: '#fb923c', bg: '#3d2000' },
    'SIN':              { color: '#60a5fa', bg: '#1a2a3d' },
    'SIN_SL':           { color: '#3b82f6', bg: '#0f1e33' },
    'SOUT':             { color: '#a78bfa', bg: '#28183d' },
    'ARMEXIT':          { color: '#fbbf24', bg: '#2e2200' },
    'LEVELLOCK_SCALE':  { color: '#818cf8', bg: '#1e1b4b' },
    'LEVELLOCK_EXIT':   { color: '#6366f1', bg: '#16153a' },
    'HEDGE_OPEN_BUY':   { color: '#fde047', bg: '#2e2800' },
    'HEDGE_OPEN_SELL':  { color: '#fde047', bg: '#2e2800' },
    'HEDGE_CLOSE_BUY':  { color: '#eab308', bg: '#251f00' },
    'HEDGE_CLOSE_SELL': { color: '#eab308', bg: '#251f00' },
    'HEDGE_TRIM_BUY':   { color: '#fb923c', bg: '#3d2000' },
    'HEDGE_TRIM_SELL':  { color: '#fb923c', bg: '#3d2000' },
};

/* PTP_* prefix → Teal */
const strategyMeta = (name) => {
    if (!name) return { color: '#848e9c', bg: '#2a2d35' };
    if (STRATEGY_COLOR_MAP[name]) return STRATEGY_COLOR_MAP[name];
    if (name.startsWith('PTP_')) return { color: '#2dd4bf', bg: '#0d2a2a' };
    if (name.startsWith('HEDGE_')) return { color: '#fde047', bg: '#2e2800' };
    return { color: '#848e9c', bg: '#2a2d35' };
};

/* Strategy filter dropdown (exact backend values + display labels) */
const STRATEGY_FILTER_OPTIONS = [
    { value: 'TP',               label: 'TP · Take Profit' },
    { value: 'SL',               label: 'SL · Stop Loss' },
    { value: 'TSL',              label: 'TSL · Trailing Stop Loss' },
    { value: 'SIN',              label: 'SIN · Scale In' },
    { value: 'SIN_SL',           label: 'SIN_SL · Scale In SL' },
    { value: 'SOUT',             label: 'SOUT · Scale Out' },
    { value: 'ARMEXIT',          label: 'ARMEXIT · Armed Exit' },
    { value: 'LEVELLOCK_SCALE',  label: 'LEVELLOCK_SCALE · Level Lock Scale' },
    { value: 'LEVELLOCK_EXIT',   label: 'LEVELLOCK_EXIT · Level Lock Exit' },
    { value: 'HEDGE_OPEN_BUY',   label: 'HEDGE_OPEN_BUY · Hedge Open BUY' },
    { value: 'HEDGE_OPEN_SELL',  label: 'HEDGE_OPEN_SELL · Hedge Open SELL' },
    { value: 'HEDGE_CLOSE_BUY',  label: 'HEDGE_CLOSE_BUY · Hedge Close BUY' },
    { value: 'HEDGE_CLOSE_SELL', label: 'HEDGE_CLOSE_SELL · Hedge Close SELL' },
    { value: 'HEDGE_TRIM_BUY',   label: 'HEDGE_TRIM_BUY · Hedge Trim BUY' },
    { value: 'HEDGE_TRIM_SELL',  label: 'HEDGE_TRIM_SELL · Hedge Trim SELL' },
];

const CELL_STYLE = { fontSize: '12px', padding: '6px 10px', whiteSpace: 'nowrap' };
const HEAD_STYLE = { fontSize: '11px', fontWeight: 700, padding: '8px 10px', whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.05em' };

/* ── Component ────────────────────────────────────────────── */
const DynamicTradeHistory = () => {
    const { control, setValue, getValues, watch } = useForm({
        defaultValues: { clientId: '', symbol: '', strategyName: '', ptpLevel: '', fromDate: '', toDate: '' }
    });

    const watchFromDate = watch('fromDate');
    const watchToDate   = watch('toDate');

    const [clientList, setClientList]             = useState([]);
    const [dynamicTradeHistory, setDynamicTradeHistory] = useState([]);
    const [isLoading, setIsLoading]               = useState(false);
    const [page, setPage]                         = useState(0);
    const [rowsPerPage, setRowsPerPage]           = useState(10);
    const [totalRecords, setTotalRecords]         = useState(0);

    useEffect(() => { getClientList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const getClientList = async () => {
        try {
            setIsLoading(true);
            const response = await ClientService.getClientList();
            const clients = response?.data ?? [];
            setClientList(clients);
            if (clients.length > 0) {
                setValue('clientId', clients[0].id);
                getDynamicTradeHistory(0, rowsPerPage);
            }
        } catch (error) {
            console.error('Error fetching clients list:', error);
            setClientList([]);
        } finally {
            setIsLoading(false);
        }
    };

    const getDynamicTradeHistory = async (pg, rpp) => {
        try {
            setIsLoading(true);
            const { clientId, symbol, strategyName, ptpLevel, fromDate, toDate } = getValues();
            const payload = { clientId };
            if (symbol)                  payload.symbol = symbol.trim().toUpperCase();
            // ptpLevel takes priority over dropdown when filled
            const resolvedStrategy = ptpLevel.trim() || strategyName;
            if (resolvedStrategy)        payload.strategyName = resolvedStrategy;
            if (fromDate)                payload.fromDate = new Date(fromDate).toISOString();
            if (toDate)                  payload.toDate = new Date(toDate).toISOString();
            const response = await DynamicTradeService.getDynamicTradeHistory(payload, pg, rpp);
            setDynamicTradeHistory(response?.data?.historyData ?? []);
            setTotalRecords(response?.data?.totalRecords || 0);
        } catch (error) {
            console.error('Error fetching dynamic trade history:', error);
            setDynamicTradeHistory([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
        getDynamicTradeHistory(newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const rpp = +event.target.value;
        setRowsPerPage(rpp);
        setPage(0);
        getDynamicTradeHistory(0, rpp);
    };

    const handleReset = () => {
        setValue('symbol', '');
        setValue('strategyName', '');
        setValue('ptpLevel', '');
        setValue('fromDate', '');
        setValue('toDate', '');
        setPage(0);
        getDynamicTradeHistory(0, rowsPerPage);
    };

    return (
        <>
            {isLoading && <Loader />}
            <div className="table-wrapper">
                <div className="table-heading card-line">
                    <div className="heading-name">Dynamic Trade History</div>
                </div>

                {/* ── Retention notice ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    background: '#1e2329', border: '1px solid #2b3139',
                    borderRadius: '6px', padding: '9px 14px',
                    marginTop: '12px', marginBottom: '12px',
                    fontSize: '12px', color: '#848e9c', lineHeight: 1.5,
                }}>
                    <span style={{ color: '#f59e0b', fontSize: '16px', flexShrink: 0 }}>⚠</span>
                    Trade history is available for the <strong style={{ color: '#c0c6cf' }}>last 30 days</strong> only.
                    A maximum of <strong style={{ color: '#c0c6cf' }}>2,000 records</strong> are retained per account.
                </div>

                {/* ── Filter bar ── */}
                <div className="row g-2 mb-1 align-items-end">
                    {/* Client */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>Client</label>
                            <Controller
                                name="clientId"
                                control={control}
                                rules={{ required: 'Client is required' }}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <Select
                                            {...field}
                                            displayEmpty
                                            sx={{ fontSize: '0.9em' }}
                                            onChange={(e) => {
                                                field.onChange(e.target.value);
                                                setPage(0);
                                                getDynamicTradeHistory(0, rowsPerPage);
                                            }}
                                        >
                                            {clientList.map((c) => (
                                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </div>
                    </div>

                    {/* Symbol */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>Symbol</label>
                            <Controller
                                name="symbol"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. BTCUSDT"
                                        autoComplete="off"
                                        sx={{ fontSize: '0.9em', '& .MuiInputBase-input': { fontSize: '0.9em' } }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Strategy */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>Strategy</label>
                            <Controller
                                name="strategyName"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth size="small">
                                        <Select {...field} displayEmpty sx={{ fontSize: '0.9em' }}>
                                            <MenuItem value="">All Strategies</MenuItem>
                                            {STRATEGY_FILTER_OPTIONS.map((s) => (
                                                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            />
                        </div>
                    </div>

                    {/* Partial TP Level */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>Partial TP Level</label>
                            <Controller
                                name="ptpLevel"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. PTP_1.5"
                                        autoComplete="off"
                                        sx={{ fontSize: '0.9em', '& .MuiInputBase-input': { fontSize: '0.9em' } }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* From Date */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>From Date</label>
                            <Controller
                                name="fromDate"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="date"
                                        size="small"
                                        fullWidth
                                        sx={{ fontSize: '0.9em', '& .MuiInputBase-input': { fontSize: '0.9em', colorScheme: 'dark' } }}
                                        slotProps={{ htmlInput: { style: { colorScheme: 'dark' } } }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* To Date */}
                    <div className="col-12 col-sm-6 col-lg-3">
                        <div className="form-control-wrapper" style={{ padding: '0px 5px' }}>
                            <label className="label-small" style={{ paddingLeft: '5px' }}>To Date</label>
                            <Controller
                                name="toDate"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type="date"
                                        size="small"
                                        fullWidth
                                        sx={{ fontSize: '0.9em', '& .MuiInputBase-input': { fontSize: '0.9em', colorScheme: 'dark' } }}
                                        slotProps={{ htmlInput: { style: { colorScheme: 'dark' } } }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    {/* Buttons */}
                    <div className="col-12 col-sm-6 col-lg-6">
                        <div className="form-control-wrapper" style={{ paddingBottom: '0.3em', display: 'flex', justifyContent: 'flex-end' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    disableElevation
                                    color="primary"
                                    style={{ fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em', minWidth: '90px' }}
                                    onClick={() => { setPage(0); getDynamicTradeHistory(0, rowsPerPage); }}
                                >
                                    Search
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    color="inherit"
                                    style={{ fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em', opacity: 0.65, minWidth: '80px' }}
                                    onClick={handleReset}
                                >
                                    Reset
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Last 30 days hint ── */}
                {!watchFromDate && !watchToDate && (
                    <div style={{ fontSize: '11.5px', color: '#6b7280', marginBottom: '10px', fontStyle: 'italic' }}>
                        Showing last 30 days. Use date filters to view older records.
                    </div>
                )}

                {/* ── Table ── */}
                <div style={{ overflowX: 'auto' }}>
                    <Table size="small" style={{ minWidth: '1100px' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={HEAD_STYLE}>Time</TableCell>
                                <TableCell style={HEAD_STYLE}>Symbol</TableCell>
                                <TableCell style={HEAD_STYLE}>Strategy</TableCell>
                                <TableCell style={HEAD_STYLE}>Side</TableCell>
                                <TableCell style={HEAD_STYLE}>Direction</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'right' }}>Entry Price</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'right' }}>Avg Fill</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'right' }}>Qty</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'right' }}>Value (USDT)</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'right' }}>P&amp;L (USDT)</TableCell>
                                <TableCell style={{ ...HEAD_STYLE, textAlign: 'center' }}>Leverage</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dynamicTradeHistory.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" className="empty-message">
                                        <img src="images/empty.svg" alt="" />
                                        <p className="crypt-grayscale-600 pb-3">No records</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dynamicTradeHistory.map((h) => {
                                    const { color, bg } = strategyMeta(h.strategyName);
                                    const pnl = h.exitPnlUsdt != null ? Number(h.exitPnlUsdt) : null;
                                    const pnlColor = pnl == null ? '#848e9c' : pnl > 0 ? '#0ecb81' : pnl < 0 ? '#f87171' : '#848e9c';

                                    return (
                                        <TableRow key={h.orderId} hover>
                                            {/* Time */}
                                            <TableCell style={CELL_STYLE}>{fmtTime(h.executedAt)}</TableCell>

                                            {/* Symbol */}
                                            <TableCell style={{ ...CELL_STYLE, fontWeight: 600 }}>{h.symbol}</TableCell>

                                            {/* Strategy badge */}
                                            <TableCell style={CELL_STYLE}>
                                                <span style={{
                                                    display: 'inline-block', padding: '2px 8px',
                                                    borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                                                    color, background: bg, letterSpacing: '0.04em',
                                                }}>
                                                    {h.strategyName ?? '—'}
                                                </span>
                                            </TableCell>

                                            {/* positionSide LONG/SHORT */}
                                            <TableCell style={CELL_STYLE}>
                                                {h.positionSide ? (
                                                    <Chip
                                                        label={h.positionSide}
                                                        size="small"
                                                        style={{
                                                            fontSize: '11px', height: '20px', fontWeight: 600,
                                                            background: h.positionSide === 'LONG' ? '#0ecb8122' : '#f8717122',
                                                            color: h.positionSide === 'LONG' ? '#0ecb81' : '#f87171',
                                                            border: `1px solid ${h.positionSide === 'LONG' ? '#0ecb81' : '#f87171'}`,
                                                        }}
                                                    />
                                                ) : '—'}
                                            </TableCell>

                                            {/* side BUY/SELL */}
                                            <TableCell style={CELL_STYLE}>
                                                {h.side ? (
                                                    <Chip
                                                        label={h.side}
                                                        size="small"
                                                        style={{
                                                            fontSize: '11px', height: '20px', fontWeight: 600,
                                                            background: h.side === 'BUY' ? '#0ecb8122' : '#f8717122',
                                                            color: h.side === 'BUY' ? '#0ecb81' : '#f87171',
                                                            border: `1px solid ${h.side === 'BUY' ? '#0ecb81' : '#f87171'}`,
                                                        }}
                                                    />
                                                ) : '—'}
                                            </TableCell>

                                            {/* Entry Price */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'right' }}>
                                                {fmt(h.entryPrice, 4)}
                                            </TableCell>

                                            {/* Avg Fill */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'right' }}>
                                                {fmt(h.avgPrice, 4)}
                                            </TableCell>

                                            {/* Qty */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'right' }}>
                                                {fmt(h.executedQty, 4)}
                                            </TableCell>

                                            {/* Value USDT */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'right' }}>
                                                {fmt(h.cumQuoteUsdt, 2)}
                                            </TableCell>

                                            {/* P&L */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'right', fontWeight: 600, color: pnlColor }}>
                                                {pnl == null ? '—' : `${pnl > 0 ? '+' : ''}${pnl.toFixed(2)}`}
                                            </TableCell>

                                            {/* Leverage */}
                                            <TableCell style={{ ...CELL_STYLE, textAlign: 'center' }}>
                                                {h.leverage != null ? `${h.leverage}×` : '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </div>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>
        </>
    );
};

export default DynamicTradeHistory;